import { z } from 'zod'
import { Currency } from '../../../enums/currency.enum'

export const depositCellClassificationSchema = z.enum(['received', 'compound'])
export type DepositCellClassification = z.infer<typeof depositCellClassificationSchema>

export const depositCellSchema = z.object({
  month: z.number().int().min(1).max(12),
  amount: z.number().nullable(),
  classification: depositCellClassificationSchema.nullable(),
})
export type DepositCellDto = z.infer<typeof depositCellSchema>

export const depositInterestDirectionSchema = z.enum(['PAYOUT', 'COMPOUND'])
export type DepositInterestDirectionLiteral = z.infer<typeof depositInterestDirectionSchema>

export const depositStatusSchema = z.enum(['ACTIVE', 'MATURED'])
export type DepositStatusLiteral = z.infer<typeof depositStatusSchema>

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

export const depositsReportRowSchema = z.object({
  accountId: z.string().guid(),
  accountName: z.string(),
  openDate: z.string().regex(ISO_DATE_REGEX, 'openDate must be YYYY-MM-DD'),
  closeDate: z.string().regex(ISO_DATE_REGEX, 'closeDate must be YYYY-MM-DD'),
  termMonths: z.number().int().min(1),
  initialBalance: z.number(),
  interestRate: z.number(),
  interestDirection: depositInterestDirectionSchema,
  status: depositStatusSchema,
  currentBalance: z.number(),
  cumulativeInterest: z.number(),
  // mirrors the DepositDetails columns the accounts list edits — surfaces
  // here so the report can edit nextInterestAmount inline without a second
  // round-trip. nextInterestDate (YYYY-MM-DD or null) lets the right-side
  // monthly grid replace the "expected interest" placeholder with the
  // editable amount in the matching month.
  nextInterestAmount: z.number(),
  nextInterestDate: z
    .string()
    .regex(ISO_DATE_REGEX, 'nextInterestDate must be YYYY-MM-DD')
    .nullable(),
  cells: z.array(depositCellSchema).length(12),
})
export type DepositsReportRowDto = z.infer<typeof depositsReportRowSchema>

export const depositsReportTotalsSchema = z.object({
  inDeposits: z.number(),
  initialBalance: z.number(),
  interestReceived: z.number(),
  compoundInterest: z.number(),
})
export type DepositsReportTotalsDto = z.infer<typeof depositsReportTotalsSchema>

export const depositsReportSummaryResponseSchema = z.object({
  currency: z.nativeEnum(Currency),
  year: z.number().int(),
  showMatured: z.boolean(),
  totals: depositsReportTotalsSchema,
  deposits: z.array(depositsReportRowSchema),
})
export type DepositsReportSummaryResponseDto = z.infer<typeof depositsReportSummaryResponseSchema>
